import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Dataset, DimensionGroup, Brushable, PCPDimension } from '../../types';
import { createPCPScales, positionPCPAxes, createPCPPathGenerator, filterByBrushes } from '../../utils/visualization';

interface ParallelCoordinatePlotProps {
  dataset: Dataset;
  selectedGroups: DimensionGroup[];
  width?: number;
  height?: number;
  selectedItems?: string[];
  brushes?: Record<string, Brushable>;
  onBrush?: (brushes: Record<string, Brushable>) => void;
  onSelect?: (items: string[]) => void;
  showLabels?: boolean;
}

const ParallelCoordinatePlot: React.FC<ParallelCoordinatePlotProps> = ({
  dataset,
  selectedGroups,
  width = 800,
  height = 500,
  selectedItems = [],
  brushes = {},
  onBrush,
  onSelect,
  showLabels = true,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{ x: number; y: number; content: string } | null>(null);
  const [dimensions, setDimensions] = useState<PCPDimension[]>([]);

  // Extract all dimensions from selected groups
  useEffect(() => {
    if (!dataset || !selectedGroups.length) return;

    // Get unique dimension IDs from all selected groups
    const dimensionIds = Array.from(new Set(selectedGroups.flatMap((group) => group.dimensions)));

    // Get the actual dimension objects
    const selectedDimensions = dimensionIds
      .map((id) => dataset.dimensions.find((d) => d.id === id))
      .filter((d) => d !== undefined) as PCPDimension[];

    // Create scales for each dimension
    const pcpDimensions = createPCPScales(selectedDimensions, height - 60);

    // Position axes horizontally
    const positionedDimensions = positionPCPAxes(pcpDimensions, width - 80);

    setDimensions(positionedDimensions);
  }, [dataset, selectedGroups, width, height]);

  // Main rendering effect
  useEffect(() => {
    if (!svgRef.current || !dataset || !dimensions.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Calculate dimensions
    const margin = { top: 30, right: 40, bottom: 30, left: 40 };
    const innerHeight = height - margin.top - margin.bottom;

    // Create the SVG group with margins
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create a path generator for the polylines
    const pathGenerator = createPCPPathGenerator(dimensions);

    // Filter items based on brushes
    const filteredItemIds =
      Object.keys(brushes).length > 0 ? filterByBrushes(dataset, brushes) : dataset.items.map((item) => item.id);

    // Draw axes
    const axesGroup = g.append('g').attr('class', 'axes');

    dimensions.forEach((dim) => {
      const axisGroup = axesGroup
        .append('g')
        .attr('class', 'pcp-axis')
        .attr('transform', `translate(${dim.x || 0}, 0)`);

      // Create axis
      const axis = d3.axisLeft(dim.scale).ticks(5);
      axisGroup.call(axis);

      // Add label if showLabels is true
      if (showLabels) {
        axisGroup
          .append('text')
          .attr('class', 'axis-label')
          .attr('y', -10)
          .attr('text-anchor', 'middle')
          .text(dim.name);
      }

      // Add brush
      const brush = d3
        .brushY()
        .extent([
          [-8, 0],
          [8, innerHeight],
        ])
        .on('end', (event) => {
          if (!event.selection) {
            // Remove this brush
            if (brushes[dim.id] && onBrush) {
              const newBrushes = { ...brushes };
              delete newBrushes[dim.id];
              onBrush(newBrushes);
            }
            return;
          }

          // Convert pixel selection to data domain
          const selection = event.selection as [number, number];
          const domainSelection: [number, number] = [dim.scale.invert(selection[1]), dim.scale.invert(selection[0])];

          // Update brushes
          if (onBrush) {
            onBrush({
              ...brushes,
              [dim.id]: {
                dimension: dim.id,
                extent: domainSelection,
              },
            });
          }
        });

      const brushGroup = axisGroup.append('g').attr('class', 'brush').call(brush);

      // Set initial brush position if exists
      if (brushes[dim.id]) {
        const brushExtent = brushes[dim.id].extent;
        const pixelExtent: [number, number] = [dim.scale(brushExtent[1]), dim.scale(brushExtent[0])];
        brushGroup.call(brush.move, pixelExtent);
      }
    });

    // Draw polylines
    const linesGroup = g.append('g').attr('class', 'lines');

    dataset.items.forEach((item) => {
      const isFiltered = !filteredItemIds.includes(item.id);
      const isSelected = selectedItems.includes(item.id);

      linesGroup
        .append('path')
        .attr('class', `pcp-polyline ${isFiltered ? 'filtered' : ''} ${isSelected ? 'selected' : ''}`)
        .attr('d', pathGenerator(item))
        .style('stroke', () => {
          // Color by group if only one group is selected
          if (selectedGroups.length === 1) {
            return '#38bdf8'; // Primary color
          }
          return '#6b7280'; // Default gray
        })
        .on('mouseover', (event) => {
          // Show tooltip
          const [x, y] = d3.pointer(event, svg.node());

          // Create tooltip content
          let content = `<div class="viz-tooltip-title">${item.label || item.id}</div>`;
          dimensions.forEach((dim) => {
            content += `<div class="viz-tooltip-row">
              <span class="viz-tooltip-label">${dim.name}:</span>
              <span>${item[dim.id]}</span>
            </div>`;
          });

          setTooltipData({
            x,
            y,
            content,
          });
        })
        .on('mouseout', () => {
          setTooltipData(null);
        })
        .on('click', () => {
          if (onSelect) {
            // Toggle selection
            const newSelection = selectedItems.includes(item.id)
              ? selectedItems.filter((id) => id !== item.id)
              : [...selectedItems, item.id];

            onSelect(newSelection);
          }
        });
    });
  }, [dataset, dimensions, width, height, brushes, selectedItems, selectedGroups, onBrush, onSelect, showLabels]);

  return (
    <div className="pcp-container" style={{ position: 'relative' }}>
      <svg ref={svgRef}></svg>

      {tooltipData && (
        <div
          className="viz-tooltip"
          style={{
            position: 'absolute',
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
          dangerouslySetInnerHTML={{ __html: tooltipData.content }}
        />
      )}

      {!dimensions.length && (
        <div className="viz-placeholder">
          <div className="viz-placeholder-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <p>Select dimension groups from the summary view above to visualize data.</p>
        </div>
      )}
    </div>
  );
};

export default ParallelCoordinatePlot;
